import { Capacitor } from '@capacitor/core';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import type { ShareableField } from '../types';
import { getPublicAppOrigin } from './publicAppUrl';
import { SaveDownload } from './saveDownload';

export const PUBLIC_PASSPORT_FIELD_OPTIONS: {
  field: ShareableField;
  label: string;
  description: string;
}[] = [
  { field: 'photo', label: 'Profile Photo', description: 'Your avatar/profile picture' },
  { field: 'full_name', label: 'Full Name', description: 'First and last name' },
  { field: 'bio', label: 'Bio / Summary', description: 'Your professional summary' },
  { field: 'email', label: 'Email Address', description: 'Your contact email' },
  { field: 'phone', label: 'Phone Number', description: 'Your contact phone number' },
  { field: 'work_experience', label: 'Work Experience', description: 'Your full employment history' },
  { field: 'qualifications', label: 'Qualifications', description: 'Certifications, degrees and licenses' },
  { field: 'references', label: 'References', description: 'Your professional references' },
  { field: 'official_documents', label: 'Official Documents', description: 'ID document and police clearance uploads' },
];

export const DEFAULT_PUBLIC_PASSPORT_FIELDS: ShareableField[] = [
  'photo',
  'full_name',
  'bio',
  'work_experience',
  'qualifications',
];

export function buildPublicPassportUrl(token: string): string {
  return `${getPublicAppOrigin()}/p/${token}`;
}

export function employeeQrFileSlug(
  firstName?: string | null,
  lastName?: string | null,
  token?: string,
): string {
  const name = [firstName, lastName]
    .filter(Boolean)
    .join('-')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
  const suffix = token ? token.slice(0, 8) : 'qr';
  return `${name || 'passport'}-${suffix}`;
}

export async function generatePassportQrPng(url: string, width = 1024): Promise<string> {
  const QRCode = (await import('qrcode')).default;
  return QRCode.toDataURL(url, {
    width,
    margin: 2,
    errorCorrectionLevel: 'M',
  });
}

export async function generatePassportQrSvg(url: string): Promise<string> {
  const QRCode = (await import('qrcode')).default;
  return QRCode.toString(url, {
    type: 'svg',
    width: 1024,
    margin: 2,
    errorCorrectionLevel: 'M',
  });
}

function triggerBrowserDownload(href: string, filename: string): void {
  const link = document.createElement('a');
  link.href = href;
  link.download = filename;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

function isShareCancelled(error: unknown): boolean {
  const message = typeof error === 'string' ? error : error instanceof Error ? error.message : '';
  return /cancel/i.test(message);
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result ?? '');
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error('Could not read the file'));
    reader.readAsDataURL(blob);
  });
}

function safeFilename(filename: string): string {
  return filename.replace(/[^\w.\-]+/g, '_') || 'download';
}

function mimeType(blob: Blob, filename: string): string {
  const fromBlob = blob.type.split(';')[0]?.trim();
  if (fromBlob) return fromBlob;
  if (filename.endsWith('.pdf')) return 'application/pdf';
  if (filename.endsWith('.svg')) return 'image/svg+xml';
  if (filename.endsWith('.png')) return 'image/png';
  if (filename.endsWith('.csv')) return 'text/csv';
  if (filename.endsWith('.zip')) return 'application/zip';
  return 'application/octet-stream';
}

function showDownloadToast(message: string): void {
  document.getElementById('download-toast')?.remove();
  const toast = document.createElement('div');
  toast.id = 'download-toast';
  toast.setAttribute('role', 'status');
  toast.textContent = message;
  toast.style.cssText = [
    'position:fixed',
    'left:16px',
    'right:16px',
    'bottom:96px',
    'z-index:80',
    'background:#0f172a',
    'color:#fff',
    'padding:12px 16px',
    'border-radius:12px',
    'font-size:14px',
    'text-align:center',
    'box-shadow:0 8px 24px rgba(15,23,42,0.25)',
  ].join(';');
  document.body.appendChild(toast);
  window.setTimeout(() => toast.remove(), 2800);
}

async function saveToDownloads(blob: Blob, filename: string): Promise<void> {
  await SaveDownload.save({
    filename,
    data: await blobToBase64(blob),
    mimeType: mimeType(blob, filename),
  });
  showDownloadToast('Saved to Downloads');
}

async function shareNativeFile(blob: Blob, filename: string): Promise<void> {
  const written = await Filesystem.writeFile({
    path: filename,
    data: await blobToBase64(blob),
    directory: Directory.Cache,
  });
  try {
    await Share.share({
      title: filename,
      files: [written.uri],
      dialogTitle: 'Save file',
    });
  } catch (error) {
    if (isShareCancelled(error)) return;
    throw error;
  }
}

export async function downloadDataUrl(dataUrl: string, filename: string): Promise<void> {
  if (Capacitor.isNativePlatform()) {
    const response = await fetch(dataUrl);
    await downloadBlob(await response.blob(), filename);
    return;
  }
  triggerBrowserDownload(dataUrl, filename);
}

export async function downloadBlob(blob: Blob, filename: string): Promise<void> {
  const safeName = safeFilename(filename);
  if (Capacitor.getPlatform() === 'android') {
    await saveToDownloads(blob, safeName);
    return;
  }
  if (Capacitor.isNativePlatform()) {
    await shareNativeFile(blob, safeName);
    return;
  }
  const url = URL.createObjectURL(blob);
  triggerBrowserDownload(url, filename);
  window.setTimeout(() => URL.revokeObjectURL(url), 1500);
}

export function csvEscape(value: string): string {
  if (/[",\n\r]/.test(value)) return `"${value.replace(/"/g, '""')}"`;
  return value;
}

export function buildPublicPassportCsv(
  rows: Array<{
    name: string;
    email: string;
    url: string;
    visible: boolean;
  }>,
): string {
  const header = 'name,email,url,visible';
  const lines = rows.map(row =>
    [
      csvEscape(row.name),
      csvEscape(row.email),
      csvEscape(row.url),
      row.visible ? 'yes' : 'no',
    ].join(','),
  );
  return [header, ...lines].join('\n');
}
